import { toast } from "react-toastify";

import { updateBlueprintId, initSetup, initJob, resetConstraints } from "containers/Setup/reducer";

import { createUserInfoQuery } from "containers/Login/reducer";

import { isEnabled } from "utils/featureFlags";

import * as blueprintReducer from "containers/Blueprint/reducer";
import * as runReducer from "containers/Run/reducer";
import * as setupReducer from "containers/Setup/reducer";
import * as homeReducer from "containers/Home/reducer";
import * as foldersReducer from "containers/Folders/reducer";

export const AIBLE_MODES = {
  ACADEMIC: "academic",
  QUICK_RUN: "quick run",
  AIBLE: "aible",
  CUSTOM: "custom"
};

export const MODEL_TYPES = {
  REGRESSION: "numerical",
  CLASSIFICATION: "categorical"
};

export const WIZARD_SECTIONS = {
  REQUIREMENTS: "Requirements Gathering",
  DATASET_RECIPES: "Data Recipes",
  DATASET_ENHANCEMENT: "Data Enhancement",
  RESULTS: "Model Selection"
};

export const DATASET_OPTIONS = {
  CHOOSE_DATASET: "Choose Data",
  FIELDS: "Fields",
  DESCRIBE: "Describe",
  INTERACTIONS: "Clusters",
  CALCULATED_FIELDS: "Calculated Fields",
  DATA_CLEANSING: "Data Cleansing"
};

export const ACADEMIC_DATASET_OPTIONS = {
  DATA_UPDATES: "Data Updates",
  FIELDS: "Fields",
  INTERACTIONS: "Clusters",
  CALCULATED_FIELDS: "Calculated Fields",
  DATA_CLEANSING: "Data Cleansing"
};

export const DATA_MODE_DATASET_OPTIONS = {
  SELECT_SUCCESS: "Choose Data",
  FIELDS: "Fields"
};

export const BUSINESS_CHOOSE_SUCCESS_OPTIONS = {
  CHOOSE_SUBGROUPS: "Choose Subgroups",
  FIELDS: "Fields"
};

export const STEP_TYPE = {
  ACADEMIC_REVIEW_DATASET: {
    id: "Academic Review Dataset",
    name: "Data Enhancement: Review Updates",
    section: WIZARD_SECTIONS.DATASET_ENHANCEMENT,
    isEnabled: (state, props) =>
      setupReducer.getDatasetFromSetup(state, props.id) &&
      (setupReducer.getSuccess(state, props.id) !== undefined ||
        setupReducer.getOutcomeType(state, props.id) === "number"),
    stepNum: 4,
    isHiddenFromMenu: true,
    tabs: ACADEMIC_DATASET_OPTIONS,
    hideTabs: (state, props) => !setupReducer.getDatasetFromSetup(state, props.id)
  },
  ACADEMIC_CHOOSE_DATASET: {
    // Used in UnifiedProject
    id: "Academic Choose Dataset",
    name: "Data Enhancement: What to Predict",
    section: WIZARD_SECTIONS.DATASET_RECIPES,
    isEnabled: (state, props) => {
      const dataset = setupReducer.getDatasetFromSetup(state, props.id);
      return (
        dataset &&
        (dataset.dataset_status === "DONE" ||
          dataset.status_name === "DONE" ||
          (dataset.status && dataset.status.length === 1 && dataset.status[0] === "indexed")) &&
        (setupReducer.getSuccess(state, props.id) !== undefined ||
          setupReducer.getOutcomeType(state, props.id) === "number")
      );
    },
    stepNum: 4,
    isHiddenFromMenu: true,
    init: (dispatch, getState, args) => {
      dispatch(setupReducer.resetForAcademic(args.runId));
      return Promise.resolve();
    }
  },
  ACADEMIC_SUCCESS: {
    id: "Academic Model Recommendation",
    name: "Model Selection",
    section: WIZARD_SECTIONS.RESULTS,
    init: (dispatch, getState, args) => {
      if (foldersReducer.getUserEditPermission(getState(), foldersReducer.getSelectedFolder(getState())) === false) {
        toast("You do not have permission to create a project in this folder.", {
          type: toast.TYPE.ERROR,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500
        });

        return Promise.resolve();
      } else if (!args.isPostRun) {
        dispatch(setupReducer.resetForAcademic(args.runId));
        return dispatch(setupReducer.initJobFromWizard(args.runId, args.blueprintId, args.options));
      } else {
        return dispatch(setupReducer.initJobFromWizard(args.runId, args.blueprintId, args.options));
      }
    },
    nextName: "Train Models",
    stepNum: 7,
    isHiddenFromMenu: true,
    postRunStep: true,
    createStep: true,
    hideNext: true
  },
  ACADEMIC_EVALUATION: {
    id: "Academic Model Evaluation",
    name: "Model Evaluation",
    section: WIZARD_SECTIONS.RESULTS,
    nextName: "Evaluate Models",
    showBlueprintCreate: true,
    stepNum: 7,
    tabs: {
      PREDICTION_DRIVERS: "Prediction Drivers",
      WHAT_IF: "What If",
      MODEL_PLOTS: "Model Plots",
      MODEL_DETAILS: "Model Details"
    },
    isHiddenFromMenu: true,
    postRunStep: true
  },
  BUSINESS_CHOOSE_DATASET: {
    id: "Business Choose Dataset",
    name: "Data Recipes: Get Data",
    section: WIZARD_SECTIONS.DATASET_RECIPES,
    isEnabled: (state, props) =>
      setupReducer.getIsValidCustomMappings(state, props.id) &&
      setupReducer.getDatasetFromSetup(state, props.id) &&
      setupReducer.getBlueprintId(state, props.id),
    stepNum: 3,
    isHiddenFromMenu: true
  },
  BUSINESS_CHOOSE_SUCCESS: {
    id: "Business Choose Success",
    name: "Data Enhancement: What to Predict",
    section: WIZARD_SECTIONS.DATASET_RECIPES,
    isEnabled: (state, props) => {
      const subgroups = setupReducer.getBusinessSubgroups(state, props.id);
      return (
        setupReducer.getDatasetFromSetup(state, props.id) &&
        (setupReducer.getSuccess(state, props.id) !== undefined ||
          setupReducer.getOutcomeType(state, props.id) === "number") &&
        subgroups &&
        subgroups[0].value &&
        subgroups[1].value
      );
    },
    tabs: BUSINESS_CHOOSE_SUCCESS_OPTIONS,
    stepNum: 4,
    isHiddenFromMenu: true
  },
  BUSINESS_IMPACTS: {
    id: "Business Cost / Benefit Tradeoff",
    name: "Requirement Gathering: Cost / Benefits",
    section: WIZARD_SECTIONS.REQUIREMENTS,
    stepNum: 1,
    isHiddenFromMenu: true
  },
  BUSINESS_PROCESS_LIMIT: {
    id: "Business Transaction Capacity",
    name: "Requirement Gathering: Transaction Capacity",
    type: MODEL_TYPES.CLASSIFICATION,
    section: WIZARD_SECTIONS.REQUIREMENTS,
    init: (dispatch, getState, args) => {
      const processLimit = setupReducer.getProcessLimit(getState(), args.runId);

      if (!processLimit) {
        const total = setupReducer.getTotal(getState(), args.runId);
        if (total) {
          dispatch(setupReducer.updateProcessLimit(args.runId, total, true));
        } else {
          dispatch(setupReducer.introspectCounts(args.runId));
        }
      }

      return Promise.resolve();
    },
    stepNum: 1,
    isHiddenFromMenu: true
  },
  BUSINESS_SUCCESS: {
    id: "Business Model Recommendation",
    name: "Model Selection",
    section: WIZARD_SECTIONS.RESULTS,
    init: (dispatch, getState, args) =>
      dispatch(setupReducer.initJobFromWizard(args.runId, args.blueprintId, args.options)),
    nextName: "Train Optimizer",
    stepNum: 7,
    isHiddenFromMenu: true
  },
  DATA_MODE_CHOOSE_DATASET: {
    // Not Used anymore
    id: "Data Mode Choose Dataset",
    name: "Data Enhancement: What to Predict",
    section: WIZARD_SECTIONS.DATASET_RECIPES,
    isEnabled: (state, props) => setupReducer.getDatasetFromSetup(state, props.id),
    stepNum: 4,
    isHiddenFromMenu: true,
    init: (dispatch, getState, args) => {
      dispatch(setupReducer.resetForAcademic(args.runId));
      return Promise.resolve();
    }
  },
  DATA_MODE_REVIEW_DATASET: {
    id: "Data Mode Review Dataset",
    name: "Data Enhancement: Review Dataset",
    section: WIZARD_SECTIONS.DATASET_ENHANCEMENT,
    stepNum: 4,
    isHiddenFromMenu: true,
    tabs: DATA_MODE_DATASET_OPTIONS,
    hideTabs: (state, props) => !setupReducer.getDatasetFromSetup(state, props.id)
  },
  DATA_MODE_SUCCESS: {
    id: "Data Mode Redirect",
    name: "Finish Dataset Review",
    section: WIZARD_SECTIONS.RESULTS,
    init: (dispatch, getState, args) => {
      const metrics = setupReducer.getAiReadyMetrics(getState(), args.runId);
      const ready = metrics && metrics.isAiReady;
      if (ready) {
        return window.open(
          "https://enaible.aible.com/aible-data-data-is-ai-ready" + createUserInfoQuery(getState()),
          "_blank"
        );
      } else {
        return;
      }
    },
    getNextName: (state, props) => {
      const metrics = setupReducer.getAiReadyMetrics(state, props.id);
      const ready = metrics && metrics.isAiReady;
      if (ready) {
        return "Get Aible and create an AI with this dataset";
      } else {
        return "Upload another dataset";
      }
    },
    stepNum: 4,
    isHiddenFromMenu: true,
    postRunStep: true,
    hideNext: true
  },
  CHOOSE_DATASET: {
    id: "Choose Dataset",
    name: "Data Recipes: Get Data",
    section: WIZARD_SECTIONS.DATASET_RECIPES,
    isEnabled: (state, props) =>
      setupReducer.getIsValidCustomMappings(state, props.id) &&
      setupReducer.getDatasetFromSetup(state, props.id) !== undefined,
    stepNum: 3,
    tabs: DATASET_OPTIONS
  },
  CHOOSE_COLUMNS: {
    id: "Specify Columns",
    name: "Data Enhancement: Specify Columns",
    section: WIZARD_SECTIONS.DATASET_ENHANCEMENT,
    stepNum: 4,
    isHiddenFromMenu: true
  },
  CORRELATION: {
    id: "Problematic Predictors",
    name: "Data Enhancement: Problematic Predictors",
    section: WIZARD_SECTIONS.DATASET_ENHANCEMENT,
    stepNum: 4
  },
  CLUSTER: {
    id: "Cluster",
    name: "Data Enhancement: Interaction Explorer",
    type: MODEL_TYPES.CLASSIFICATION,
    section: WIZARD_SECTIONS.DATASET_ENHANCEMENT,
    stepNum: 4,
    isHiddenFromMenu: true
  },
  INTERACTION: {
    id: "Interaction Graph",
    name: "Data Enhancement: Interaction Graph",
    type: MODEL_TYPES.CLASSIFICATION,
    section: WIZARD_SECTIONS.DATASET_ENHANCEMENT,
    stepNum: 4,
    isHiddenFromMenu: true
  },
  CHOOSE_SUCCESS: {
    id: "What to Predict",
    name: "Data Enhancement: What to Predict",
    type: MODEL_TYPES.CLASSIFICATION,
    section: WIZARD_SECTIONS.DATASET_RECIPES,
    isEnabled: (state, props) => setupReducer.getSuccess(state, props.id) !== undefined,
    stepNum: 4
  },
  COSTS_SLIDER: {
    id: "Cost / Benefit Tradeoff (Simple)",
    name: "Requirement Gathering: Cost / Benefits (Simple)",
    section: WIZARD_SECTIONS.REQUIREMENTS,
    stepNum: 1
  },
  RAW_COSTS: {
    id: "Cost / Benefit Tradeoff (Classification)",
    name: "Requirement Gathering: Cost / Benefits (Categorical)",
    type: MODEL_TYPES.CLASSIFICATION,
    section: WIZARD_SECTIONS.REQUIREMENTS,
    stepNum: 1
  },
  ADVANCED_COSTS: {
    id: "Cost / Benefit Tradeoff (Advanced)",
    name: "Requirement Gathering: Cost / Benefits (Advanced)",
    section: WIZARD_SECTIONS.REQUIREMENTS,
    stepNum: 1
  },
  COSTS_WATERFALL: {
    id: "Counts",
    name: "Requirement Gathering: Current State",
    type: MODEL_TYPES.CLASSIFICATION,
    section: WIZARD_SECTIONS.REQUIREMENTS,
    stepNum: 1
  },
  UNDERSTAND_ACTIONS: {
    id: "Understanding Actions",
    name: "Requirement Gathering: Understanding Actions",
    type: MODEL_TYPES.CLASSIFICATION,
    section: WIZARD_SECTIONS.REQUIREMENTS,
    stepNum: 1
  },
  PROCESS_LIMIT: {
    id: "Transaction Capacity",
    name: "Requirement Gathering: Transaction Capacity",
    type: MODEL_TYPES.CLASSIFICATION,
    section: WIZARD_SECTIONS.REQUIREMENTS,
    init: (dispatch, getState, args) => {
      const processLimit = setupReducer.getProcessLimit(getState(), args.runId);

      if (processLimit === 0) {
        const total = setupReducer.getTotal(getState(), args.runId);
        dispatch(setupReducer.updateProcessLimit(args.runId, total, true));
      }

      return Promise.resolve();
    },
    stepNum: 1,
    isHiddenFromMenu: true
  },

  CONTINUOUS_IMPACTS: {
    id: "Cost / Benefit Tradeoff (Regression)",
    name: "Requirement Gathering: Cost / Benefits (Numerical)",
    type: MODEL_TYPES.REGRESSION,
    section: WIZARD_SECTIONS.REQUIREMENTS,
    stepNum: 1
  },
  CONTINUOUS_COSTS: {
    id: "Close Enough Threshold",
    name: "Data Enhancement: Close Enough Threshold",
    type: MODEL_TYPES.REGRESSION,
    section: WIZARD_SECTIONS.DATASET_ENHANCEMENT,
    stepNum: 4
  },

  CHOOSE_OPTIONS: {
    id: "Model Type Restrictions",
    name: "Requirement Gathering: Type Restrictions",
    section: WIZARD_SECTIONS.REQUIREMENTS,
    stepNum: 1
  },
  TIME_PERIOD: {
    id: "Time Period",
    name: "Requirement Gathering: Time Period",
    section: "none",
    stepNum: 1
  },
  UNITS: {
    id: "Units",
    name: "Requirement Gathering: Units",
    section: WIZARD_SECTIONS.REQUIREMENTS,
    stepNum: 1
  },

  CONTEST: {
    id: "Model Card Contest",
    name: "Model Selection: Contest",
    section: WIZARD_SECTIONS.RESULTS,
    stepNum: 7,
    isHiddenFromMenu: true
  },
  SUCCESS: {
    id: "Model Recommendation",
    name: "Model Selection",
    section: WIZARD_SECTIONS.RESULTS,
    init: (dispatch, getState, args) => {
      if (foldersReducer.getUserEditPermission(getState(), foldersReducer.getSelectedFolder(getState())) === false) {
        toast("You do not have permission to create a project in this folder.", {
          type: toast.TYPE.ERROR,
          position: toast.POSITION.TOP_CENTER,
          autoClose: 7500
        });
        return Promise.resolve();
      } else if (!args.isPostRun) {
        return dispatch(setupReducer.initJobFromWizard(args.runId, args.blueprintId, args.options));
      }
    },
    nextName: "Train Models",
    stepNum: 7,
    tabs: {
      OVERVIEW: "Overview",
      ROI_CHART: "ROI Chart",
      PREDICTION_DRIVERS: "Prediction Drivers",
      WHAT_IF: "What If",
      MODEL_DETAILS: "Model Details"
    },
    createStep: true,
    postRunStep: true
  },
  MODEL_DEPLOYMENT: {
    id: "Model Deployment",
    name: "Model Predictions and Deployment",
    section: WIZARD_SECTIONS.RESULTS,
    nextName: "Get Predictions",
    init: (dispatch, getState, args) => {
      dispatch(runReducer.updateIsNextStep(args.runId, true));
    },
    stepNum: 8,
    isHiddenFromMenu: true,
    postRunStep: true
  }
};

export const REMOVED_STEPS = [
  STEP_TYPE.PROCESS_LIMIT.id,
  STEP_TYPE.CHOOSE_COLUMNS.id,
  STEP_TYPE.TIME_PERIOD.id,
  STEP_TYPE.CLUSTER.id
];

export const ORIGINAL_STEPS = {
  ACADEMIC: [
    STEP_TYPE.ACADEMIC_REVIEW_DATASET.id,
    STEP_TYPE.ACADEMIC_SUCCESS.id,
    STEP_TYPE.ACADEMIC_EVALUATION.id,
    STEP_TYPE.MODEL_DEPLOYMENT.id
  ],
  BUSINESS_CONTINUOUS: [
    STEP_TYPE.BUSINESS_CHOOSE_DATASET.id,
    STEP_TYPE.BUSINESS_CHOOSE_SUCCESS.id,
    STEP_TYPE.BUSINESS_IMPACTS.id,
    STEP_TYPE.SUCCESS.id
  ],
  DATA_MODE: [STEP_TYPE.DATA_MODE_REVIEW_DATASET.id, STEP_TYPE.DATA_MODE_SUCCESS.id],
  QUICK_CONTINUOUS: [
    STEP_TYPE.COSTS_SLIDER.id,
    STEP_TYPE.CHOOSE_DATASET.id,
    STEP_TYPE.CHOOSE_COLUMNS.id,
    STEP_TYPE.SUCCESS.id
  ],
  AIBLE_CONTINUOUS: [
    STEP_TYPE.UNITS.id,
    STEP_TYPE.CONTINUOUS_IMPACTS.id,
    STEP_TYPE.CONTINUOUS_COSTS.id,
    STEP_TYPE.CHOOSE_DATASET.id,
    STEP_TYPE.CHOOSE_COLUMNS.id,
    STEP_TYPE.SUCCESS.id
  ],
  BUSINESS_CATEGORICAL: [
    STEP_TYPE.BUSINESS_CHOOSE_DATASET.id,
    STEP_TYPE.BUSINESS_CHOOSE_SUCCESS.id,
    STEP_TYPE.BUSINESS_IMPACTS.id,
    STEP_TYPE.BUSINESS_PROCESS_LIMIT.id,
    STEP_TYPE.SUCCESS.id
  ],
  QUICK_CATEGORICAL: [
    STEP_TYPE.COSTS_SLIDER.id,
    STEP_TYPE.CHOOSE_DATASET.id,
    STEP_TYPE.CHOOSE_COLUMNS.id,
    STEP_TYPE.CHOOSE_SUCCESS.id,
    STEP_TYPE.SUCCESS.id
  ],
  AIBLE_CATEGORICAL: [
    STEP_TYPE.RAW_COSTS.id,
    STEP_TYPE.COSTS_WATERFALL.id,
    STEP_TYPE.UNDERSTAND_ACTIONS.id,
    STEP_TYPE.PROCESS_LIMIT.id,
    STEP_TYPE.CHOOSE_DATASET.id,
    STEP_TYPE.CHOOSE_COLUMNS.id,
    STEP_TYPE.CHOOSE_SUCCESS.id,
    STEP_TYPE.SUCCESS.id
  ],
  ADVANCED_CATEGORICAL: [
    STEP_TYPE.ADVANCED_COSTS.id,
    STEP_TYPE.COSTS_WATERFALL.id,
    STEP_TYPE.UNDERSTAND_ACTIONS.id,
    STEP_TYPE.PROCESS_LIMIT.id,
    STEP_TYPE.CHOOSE_DATASET.id,
    STEP_TYPE.CHOOSE_COLUMNS.id,
    STEP_TYPE.CHOOSE_SUCCESS.id,
    STEP_TYPE.SUCCESS.id
  ],
  ADVANCED_CONTINUOUS: [
    STEP_TYPE.UNITS.id,
    STEP_TYPE.ADVANCED_COSTS.id,
    STEP_TYPE.CONTINUOUS_COSTS.id,
    STEP_TYPE.CHOOSE_DATASET.id,
    STEP_TYPE.CHOOSE_COLUMNS.id,
    STEP_TYPE.SUCCESS.id
  ]
};

export const INITIAL_BLUEPRINTS = {
  AIBLE: {
    [MODEL_TYPES.REGRESSION]: ORIGINAL_STEPS.AIBLE_CONTINUOUS,
    [MODEL_TYPES.CLASSIFICATION]: ORIGINAL_STEPS.AIBLE_CATEGORICAL,
    steps: [STEP_TYPE.CHOOSE_DATASET.id]
  },
  QUICK: {
    [MODEL_TYPES.REGRESSION]: ORIGINAL_STEPS.QUICK_CONTINUOUS,
    [MODEL_TYPES.CLASSIFICATION]: ORIGINAL_STEPS.QUICK_CATEGORICAL,
    steps: [STEP_TYPE.CHOOSE_DATASET.id]
  },
  ACADEMIC: {
    [MODEL_TYPES.REGRESSION]: ORIGINAL_STEPS.ACADEMIC,
    [MODEL_TYPES.CLASSIFICATION]: ORIGINAL_STEPS.ACADEMIC,
    steps: [STEP_TYPE.ACADEMIC_CHOOSE_DATASET.id]
  }
};

export const UPDATE_FIELD = "wizard/UPDATE_FIELD";
export const RESET = "wizard/RESET";

const initialState = {
  stepIndex: 0,
  aibleMode: undefined,
  modelType: undefined,
  hoverStep: undefined,
  isCreating: false
};

function wizardReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_FIELD:
      return {
        ...state,
        [action.key]: action.data
      };
    case RESET:
      return {
        ...initialState
      };
    default:
      return state;
  }
}

/* Selectors */
export function getBase(state) {
  return state.wizard;
}

export function getStepIndex(state, runId, originalId) {
  return getBase(state).stepIndex;
}

export function getIsCreating(state) {
  return getBase(state).isCreating;
}

export function getModelType(state, runId, blueprintId) {
  if (runId !== "new" && runId !== undefined) {
    const setup = setupReducer.getSetup(state, runId);

    return setup && setup.success ? MODEL_TYPES.CLASSIFICATION : MODEL_TYPES.REGRESSION;
  } else if (blueprintId !== "new" && blueprintId !== undefined) {
    const blueprint = blueprintReducer.getBlueprint(state, blueprintId);

    const dataset =
      blueprint && blueprint.wizard && blueprint.wizard.dataset && blueprint.wizard.dataset.outcome
        ? blueprint.wizard.dataset
        : setupReducer.getDatasetFromSetup(state, runId);

    return dataset &&
      dataset.outcome &&
      dataset.fields &&
      dataset.fields[dataset.outcome] &&
      dataset.fields[dataset.outcome].type === "categorical"
      ? MODEL_TYPES.CLASSIFICATION
      : dataset &&
          dataset.outcome &&
          dataset.fields &&
          dataset.fields[dataset.outcome] &&
          dataset.fields[dataset.outcome].type === "number"
        ? MODEL_TYPES.REGRESSION
        : getBase(state)?.modelType;
  } else {
    return getBase(state)?.modelType;
  }
}

export function getAibleMode(state, runId, blueprintId) {
  if (runId !== "new" && runId !== undefined) {
    const run = runReducer.getJob(state, runId);

    return run && run.runType && run.runType === "5 Minute"
      ? AIBLE_MODES.AIBLE
      : run.runType === "2 Minute"
        ? AIBLE_MODES.QUICK_RUN
        : AIBLE_MODES.ACADEMIC;
  } else if (blueprintId !== "new" && blueprintId !== undefined && blueprintId !== "template") {
    const blueprint = blueprintReducer.getBlueprint(state, blueprintId);

    return blueprint &&
      blueprint.wizard &&
      blueprint.wizard.setup &&
      blueprint.wizard.setup.mode !== undefined &&
      blueprint.wizard.setup.mode === 2
      ? AIBLE_MODES.AIBLE
      : blueprint && blueprint.wizard && blueprint.wizard.setup && blueprint.wizard.setup.mode === 1
        ? AIBLE_MODES.QUICK_RUN
        : AIBLE_MODES.ACADEMIC;
  } else {
    return getBase(state).aibleMode;
  }
}

export function getStepDefaultOrder(steps, modelType) {
  return Object.keys(WIZARD_SECTIONS)
    .map((w) =>
      Object.keys(STEP_TYPE)
        .filter(
          (s) =>
            (STEP_TYPE[s].type === undefined || STEP_TYPE[s].type === modelType) &&
            STEP_TYPE[s].section === WIZARD_SECTIONS[w]
        )
        .filter((s) => steps.indexOf(STEP_TYPE[s].id) !== -1)
        .map((s) => STEP_TYPE[s].id)
    )
    .reduce((a, b) => a.concat(b, []));
}

export function getSteps(state, runId, blueprintId) {
  const modelType = getModelType(state, runId, blueprintId);
  const steps = blueprintReducer.getSteps(state, runId, blueprintId);

  if (runId !== "new" && setupReducer.getSuccess(state, runId)) {
    const blueprint = blueprintReducer.getBlueprint(state, blueprintId);
  }

  /* Reorder steps into a fixed order */
  return steps
    .filter((s) => REMOVED_STEPS.indexOf(s) === -1)
    .map((s) => (s === "Choose Success" ? "What to Predict" : s)); //getStepDefaultOrder(steps, modelType);
}

export function getHoverStep(state) {
  return getBase(state).hoverStep;
}

export function getNextName(state, runId, blueprintId) {
  const steps = getSteps(state, runId, blueprintId);
  const nextStepIndex = getStepIndex(state) + 1;

  const currStepIndex = getStepIndex(state);
  let nextStep;

  Object.keys(STEP_TYPE).forEach((s) => {
    if (STEP_TYPE[s].id === steps[nextStepIndex]) {
      nextStep = STEP_TYPE[s];
    }
  });

  let retName = "Continue";

  if (steps && nextStep) {
    if (nextStep.nextName !== undefined) {
      retName = nextStep.nextName;
    }
    if (nextStep.getNextName) {
      retName = nextStep.getNextName(state, {
        id: runId,
        blueprintId: blueprintId
      });
    }
  }

  return retName;
}

export function checkIsEnabledTab(state, key, value) {
  if (!isEnabled("clustering")) {
    return !(key === "INTERACTIONS" || value === "Clusters");
  }
  return true;
}

// Probably deprecated; initStepIndex should set the stepIndex to the number we want when loading a blueprint;
export function getRealStepIndex(state, runId, blueprintId) {
  return runId === "new"
    ? getStepIndex(state)
    : runReducer.getRelatedId(state, runId) === -1
      ? getSteps(state, runId, blueprintId).length - 2 + getStepIndex(state)
      : 9999;
}

/* Actions */
export function initStepIndex(runId, bpId) {
  return (dispatch, getState) => {
    const state = getState();

    if (getIsCreating(getState()) == true) {
      dispatch(updateIsCreating(false));
    }

    if (runId === "new") {
      return dispatch(updateStepIndex(0));
    }

    const runBlueprintId = setupReducer.getBlueprintId(state, runId);
    const blueprintId = runId !== "new" ? runBlueprintId : bpId;
    const stepIds = getSteps(state, runId, blueprintId);
    let stepTypeById = {};
    for (const stepKey in STEP_TYPE) {
      const cStep = STEP_TYPE[stepKey];
      if (stepIds.includes(cStep.id)) {
        stepTypeById[cStep.id] = cStep;
      }
    }
    for (let i = 0; i < stepIds.length; i++) {
      const cStep = stepTypeById[stepIds[i]];
      if (cStep.postRunStep) {
        const postRunIndex = i;
        return dispatch(updateStepIndex(postRunIndex));
      }
    }
    return dispatch(updateStepIndex(9999));
  };
}

export function incrementStep(runId, blueprintId, index) {
  return (dispatch, getState) => {
    //const stepIndex = getStepIndex(getState());

    const steps = getSteps(getState(), runId, blueprintId);

    const nextStepIndex = index + 1;

    let nextStep = undefined;
    let currStep = undefined;

    for (const stepKey in STEP_TYPE) {
      const cStep = STEP_TYPE[stepKey];
      if (cStep.id === steps[index]) {
        currStep = cStep;
      }
      if (cStep.id === steps[nextStepIndex]) {
        nextStep = cStep;
      }
      if (currStep && nextStep) {
        break;
      }
    }

    // Prevent the wheel from showing twice
    if (nextStep && nextStep.createStep !== true) {
      dispatch(updateStepIndex(nextStepIndex));
      dispatch(scrollToStep(nextStepIndex));
    } else {
      if (foldersReducer.getUserEditPermission(getState(), foldersReducer.getSelectedFolder(getState())) !== false) {
        dispatch(updateIsCreating(true));
      }
    }

    if (nextStep && nextStep.init) {
      nextStep.init(dispatch, getState, {
        runId: runId,
        blueprintId: blueprintId,
        isPostRun: currStep.postRunStep
      });
    }

    return Promise.resolve();
  };
}

export function scrollToDataset(offset = 0) {
  return setTimeout(() => {
    if (
      document.getElementById("wizardStepScroll") &&
      document.getElementsByClassName("chooseDataset") &&
      document.getElementsByClassName("chooseDataset")[0]
    ) {
      document.getElementById("wizardStepScroll").scrollTo({
        top: document.getElementsByClassName("chooseDataset")[0].offsetTop - 160 + offset,
        behavior: "smooth"
      }),
        100;
    }
  });
}

export function scrollToStepById(id, blueprintId, stepId) {
  return (dispatch, getState) => {
    const state = getState();

    const modelType = getModelType(state, id, blueprintId);

    const steps = blueprintReducer.getSteps(state, id, blueprintId);

    const retSteps = getSteps(state, id, blueprintId);

    const index = retSteps.indexOf(stepId);

    dispatch(updateHoverStep(stepId));

    return index !== -1 && dispatch(scrollToStep(index, -150));
  };
}

export function scrollToStep(index, offset = 0) {
  return (dispatch, getState) => {
    return setTimeout(() => {
      if (document.getElementById("wizardStepScroll") && document.getElementById("wizardStep_" + index)) {
        document.getElementById("wizardStepScroll").scrollTo({
          top: document.getElementById("wizardStep_" + index).offsetTop - 160 + offset,
          behavior: "smooth"
        }),
          100;
      }
    });
  };
}

export function scrollToEnd(timeout = 100) {
  return (dispatch, getState) => {
    return setTimeout(() => {
      if (document.getElementById("wizardStepScroll")) {
        document.getElementById("wizardStepScroll").scrollTo({
          top: 999999,
          behavior: "smooth"
        });
      }
    }, timeout);
  };
}

export function init() {
  return { type: RESET };
}

export function updateStepIndex(stepIndex) {
  return { type: UPDATE_FIELD, data: stepIndex, key: "stepIndex" };
}

export function updateIsCreating(isCreating) {
  return { type: UPDATE_FIELD, data: isCreating, key: "isCreating" };
}

export function updateHoverStep(hoverStep) {
  return (dispatch, getState) => {
    return (
      getHoverStep(getState()) !== hoverStep && dispatch({ type: UPDATE_FIELD, data: hoverStep, key: "hoverStep" })
    );
  };
}

export function updateAibleModeDirect(aibleMode) {
  return { type: UPDATE_FIELD, data: aibleMode, key: "aibleMode" };
}

export function updateModelType(id, blueprintId, modelType) {
  return (dispatch, getState) => {
    dispatch({ type: UPDATE_FIELD, data: modelType, key: "modelType" });
    const aibleMode = getAibleMode(getState());

    if (aibleMode !== undefined) {
      dispatch(updateAibleMode(id, blueprintId, aibleMode));
    }

    dispatch(updateStepIndex(0));
  };

  return Promise.resolve();
}

export function updateAibleMode(id, blueprintId, aibleMode) {
  return (dispatch, getState) => {
    const modelType = getModelType(getState(), id, blueprintId);

    dispatch(updateAibleModeDirect(aibleMode));

    return dispatch(buildSteps(id, blueprintId));
  };
}

export function buildSteps(id, blueprintId) {
  return (dispatch, getState) => {
    const modelType = getModelType(getState(), id, blueprintId);
    const aibleMode = getAibleMode(getState(), id, blueprintId);

    if (modelType === MODEL_TYPES.REGRESSION) {
      if (aibleMode === AIBLE_MODES.ACADEMIC) {
        dispatch(blueprintReducer.updateSteps(blueprintId, ORIGINAL_STEPS.ACADEMIC));
      } else if (aibleMode === AIBLE_MODES.QUICK_RUN) {
        dispatch(blueprintReducer.updateSteps(blueprintId, ORIGINAL_STEPS.QUICK_CONTINUOUS));
      } else if (aibleMode === AIBLE_MODES.AIBLE) {
        dispatch(blueprintReducer.updateSteps(blueprintId, ORIGINAL_STEPS.AIBLE_CONTINUOUS));
      } else if (aibleMode === AIBLE_MODES.CUSTOM) {
        dispatch(blueprintReducer.updateSteps(blueprintId, []));
      }
    } else if (modelType === MODEL_TYPES.CLASSIFICATION) {
      if (aibleMode === AIBLE_MODES.ACADEMIC) {
        dispatch(blueprintReducer.updateSteps(blueprintId, ORIGINAL_STEPS.ACADEMIC));
      } else if (aibleMode === AIBLE_MODES.QUICK_RUN) {
        dispatch(blueprintReducer.updateSteps(blueprintId, ORIGINAL_STEPS.QUICK_CATEGORICAL));
      } else if (aibleMode === AIBLE_MODES.AIBLE) {
        dispatch(blueprintReducer.updateSteps(blueprintId, ORIGINAL_STEPS.AIBLE_CATEGORICAL));
      } else if (aibleMode === AIBLE_MODES.CUSTOM) {
        dispatch(blueprintReducer.updateSteps(blueprintId, []));
      }
    }
  };
}

export default wizardReducer;
